import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import NewProposalPage from "@/pages/NewProposalPage";
import ProposalsPage from "@/pages/ProposalsPage";
import ClientsPage from "@/pages/ClientsPage";
import HotelLibraryPage from "@/pages/HotelLibraryPage";
import TourLibraryPage from "@/pages/TourLibraryPage";
import ConfirmationsPage from "@/pages/ConfirmationsPage";
import ConfirmationEditorPage from "@/pages/ConfirmationEditorPage";
import SettingsPage from "@/pages/SettingsPage";
import ReservationsPage from "@/pages/ReservationsPage";
import ReservationEditorPage from "@/pages/ReservationEditorPage";
import RevenuePage from "@/pages/RevenuePage";
import Sidebar from "@/components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// API instance with auth
export const api = axios.create({
  baseURL: API,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      // Verify token
      api.get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("user", JSON.stringify(res.data));
        })
        .catch(() => {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const register = async (email, password, name) => {
    const res = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("token", res.data.access_token);
    localStorage.setItem("user", JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    toast.success("Logged out successfully");
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-navy border-t-transparent animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main Layout with Sidebar
const MainLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(window.innerWidth < 768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = window.innerWidth < 768;

  return (
    <div className="flex min-h-screen bg-background">
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40" onClick={() => setMobileOpen(false)} />
      )}
      <div className={`${isMobile ? 'fixed z-50 h-full transition-transform duration-200' : ''} ${isMobile && !mobileOpen ? '-translate-x-full' : 'translate-x-0'}`}>
        <Sidebar collapsed={isMobile ? false : sidebarCollapsed} onToggle={() => isMobile ? setMobileOpen(false) : setSidebarCollapsed(!sidebarCollapsed)} onNavigate={() => isMobile && setMobileOpen(false)} />
      </div>
      <main className="flex-1 overflow-auto w-full">
        {isMobile && (
          <div className="fixed top-0 left-0 right-0 z-30 bg-navy h-11 flex items-center px-3 gap-3 shadow-md">
            <button onClick={() => setMobileOpen(true)} className="text-white" data-testid="mobile-menu-btn">
              <svg width="22" height="22" viewBox="0 0 20 20" fill="none"><path d="M3 5h14M3 10h14M3 15h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
            </button>
            <span className="text-white font-serif text-sm">ProposalDesk</span>
          </div>
        )}
        {children}
      </main>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <MainLayout>
                  <Routes>
                    <Route index element={<Navigate to="/dashboard" replace />} />
                    <Route path="dashboard" element={<DashboardPage />} />
                    <Route path="proposals/new" element={<NewProposalPage />} />
                    <Route path="proposals/:id/edit" element={<NewProposalPage />} />
                    <Route path="proposals" element={<ProposalsPage />} />
                    <Route path="clients" element={<ClientsPage />} />
                    <Route path="hotels" element={<HotelLibraryPage />} />
                    <Route path="tours" element={<TourLibraryPage />} />
                    <Route path="confirmations" element={<ConfirmationsPage />} />
                    <Route path="confirmations/new" element={<ConfirmationEditorPage />} />
                    <Route path="confirmations/:id/edit" element={<ConfirmationEditorPage />} />
                    <Route path="reservations" element={<ReservationsPage />} />
                    <Route path="reservations/new" element={<ReservationEditorPage />} />
                    <Route path="reservations/:id/edit" element={<ReservationEditorPage />} />
                    <Route path="revenue" element={<RevenuePage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </MainLayout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
